import { type FunctionComponent, useState } from 'react';
import cn from 'classnames';
import { renderIsland } from './lib/renderIsland';
import { persistentStorage } from '../../../../lib/storage';
import { ErrorBlankSad, ErrorBlankSpinner } from '../../../ErrorBlank';
import { ErrorBlankTears } from '../../../ErrorBlank';
import { StatusWrapper } from '../../../StatusWrapper';
import { GridLayoutWrapper } from '../GridLayoutWrapper';
import { ResizerHorizontal } from '../ResizerHorizontal';
import { type GridPageLayoutComponentProps } from './types';
import styles from './GridPageLayout@desktop.module.css';

const GridPageLayout: FunctionComponent<GridPageLayoutComponentProps> = ({
    queryStatus,
    header,
    sidebar,
    isSidebarResizable = true,
    hasGapContainer = true,
    isCard = false,
    contentRef,
    containerRef,
    children,
    isEmpty,
    renderEmpty,
    renderEmptyIslandContent,
    isError,
    renderError,
    renderErrorIslandContent,
    isFetching,
    renderFetching,
    renderFetchingIslandContent,
    containerClassName,
    contentClassName,
}) => {
    const [sidebarWidth, setSidebarWidth] = useState(Number(persistentStorage.getItem('products.categories')) || 220);
    const [isSidebarResizing, setIsSidebarResizing] = useState(false);

    const fetching = () =>
        renderIsland({
            fullRender: renderFetching,
            renderContent: renderFetchingIslandContent,
            content: <ErrorBlankSpinner title="Идёт загрузка информации для отображения страницы..." />,
            islandContainerClassName: styles.islandContainer,
            islandClassName: styles.island,
        });

    const error = () =>
        renderIsland({
            fullRender: renderError,
            renderContent: renderErrorIslandContent,
            content: <ErrorBlankTears title="На странице что-то пошло не так" />,
            islandContainerClassName: styles.islandContainer,
            islandClassName: styles.island,
        });

    const empty = () =>
        renderIsland({
            fullRender: renderEmpty,
            renderContent: renderEmptyIslandContent,
            content: <ErrorBlankSad title="На странице нет данных" />,
            islandContainerClassName: styles.islandContainer,
            islandClassName: styles.island,
        });

    const onResize = (width: number) => {
        setSidebarWidth(width);
        persistentStorage.setItem('products.categories', String(width));
    };

    return (
        <StatusWrapper
            queryStatus={queryStatus}
            isError={isError}
            renderError={error}
            isEmpty={isEmpty}
            renderEmpty={empty}
            isFetching={isFetching}
            renderFetching={fetching}
        >
            <div
                ref={containerRef}
                className={cn(styles.container, containerClassName, {
                    [styles.dragging]: isSidebarResizing,
                    [styles.isCard]: isCard,
                    [styles.hasGapContainer]: hasGapContainer,
                    [styles.withHeader]: Boolean(header),
                    [styles.withSidebar]: Boolean(sidebar),
                })}
            >
                {Boolean(header) && <div className={styles.header}>{header}</div>}
                {isSidebarResizing && isSidebarResizable && (
                    <div className={cn(styles.content, styles.over)} style={{ width: sidebarWidth }} />
                )}
                {Boolean(sidebar) && (
                    <div className={styles.sidebar} style={{ width: sidebarWidth }}>
                        {sidebar}
                    </div>
                )}
                {Boolean(sidebar) && isSidebarResizable && (
                    <div className={styles.resizer} style={{ width: sidebarWidth }}>
                        <ResizerHorizontal
                            minWidth={150}
                            maxWidth={500}
                            value={sidebarWidth}
                            onChangeEnd={onResize}
                            className={styles.control}
                            onDragging={setIsSidebarResizing}
                        />
                    </div>
                )}
                <GridLayoutWrapper contentRef={contentRef} contentClassName={cn(styles.content, contentClassName)}>
                    {children}
                </GridLayoutWrapper>
                {isSidebarResizing && isSidebarResizable && <div className={cn(styles.content, styles.over)} />}
            </div>
        </StatusWrapper>
    );
};

export default GridPageLayout;
